'use client';

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AlertCircle, Edit2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InlineSpeakerBadgeProps {
  speakerId: string;
  currentName?: string;
  onSave: (name: string) => Promise<void>;
  isUnlabeled: boolean;
  className?: string;
}

export function InlineSpeakerBadge({
  speakerId,
  currentName,
  onSave,
  isUnlabeled,
  className,
}: InlineSpeakerBadgeProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(currentName || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const trimmedName = name.trim();

    // Require a name to be entered
    if (!trimmedName) {
      setIsEditing(false);
      setName(currentName || '');
      return;
    }

    setIsSaving(true);

    try {
      await onSave(trimmedName);
      setIsEditing(false);
    } catch (error) {
      console.error('[InlineSpeakerBadge] Save error:', error);
      // Reset on error
      setName(currentName || '');
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setName(currentName || '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="inline-flex items-center gap-1">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
          placeholder={`Speaker ${speakerId}`}
          className="h-7 w-32 text-xs"
          autoFocus
          disabled={isSaving}
        />
        {isSaving && <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
    );
  }

  return (
    <Badge
      onClick={() => setIsEditing(true)}
      className={cn(
        'cursor-pointer hover:bg-primary/20 transition-colors gap-1',
        isUnlabeled && 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700',
        className
      )}
    >
      {currentName || `Speaker ${speakerId}`}
      {isUnlabeled && <AlertCircle className="h-3 w-3 text-yellow-600 dark:text-yellow-500" />}
      <Edit2 className="h-3 w-3 opacity-50" />
    </Badge>
  );
}
